import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ShieldCheck, Trophy } from 'lucide-react';
import clsx from 'clsx';
import type { ReviewQueueItem } from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useMenuPath } from '../lib/menu-path';
import { useToast } from '../lib/toast';
import { ListShell } from '../components/ListShell';
import { KpiStrip } from '../components/shared/KpiStrip';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { MonthPicker } from '../components/teacher-reports/MonthPicker';
import { ReviewItem } from '../components/teacher-reports/review/ReviewItem';
import { academyMonth, formatPeriod } from '../components/teacher-reports/format';
import { isReportsDisabled } from '../components/teacher-reports/useDueReports';

export const Route = createFileRoute('/_authed/academics/report-review')({
  component: ReportReviewPage,
});

const REVIEWERS = ['super_admin', 'academic_admin', 'nalim'] as const;

type Tab = 'awaiting' | 'flagged' | 'changes_requested' | 'approved' | 'missed';
const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'awaiting', label: 'Awaiting review' },
  { value: 'flagged', label: 'Flagged' },
  { value: 'changes_requested', label: 'Sent back' },
  { value: 'approved', label: 'Approved' },
  { value: 'missed', label: 'Missed' },
];

function ReportReviewPage() {
  const canReview = useAuthStore((s) => s.hasRole(...REVIEWERS));
  if (!canReview) {
    return (
      <ListShell title="Reports to review">
        <EmptyState
          title="Management only"
          description="Nalims and admins review teacher reports here. Your own points are always on My points."
          icon={<ShieldCheck size={32} aria-hidden="true" />}
        />
      </ListShell>
    );
  }
  return <ReviewQueue />;
}

const hasOpenFlags = (i: ReviewQueueItem) => i.flags.some((f) => !f.resolved);

function ReviewQueue() {
  const canExcuse = useAuthStore((s) => s.hasRole('super_admin', 'academic_admin'));
  const featuresPath = useMenuPath('/system/feature-flags');
  // Reviewers can't approve their own reports (the API refuses) — keep them out of the actions.
  const myTeacherId = useAuthStore((s) => s.user?.teacherId ?? null);
  const isOwn = (i: ReviewQueueItem) => myTeacherId !== null && i.teacherId === myTeacherId;
  const [tab, setTab] = useState<Tab>('awaiting');
  const [month, setMonth] = useState(academyMonth);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const submitted = useQuery({
    queryKey: ['teacher-reports', 'review', 'queue', 'submitted'] as const,
    queryFn: ({ signal }) => apiRequest<{ items: ReviewQueueItem[] }>('/api/teacher-reports/review', { signal, query: { status: 'submitted' } }),
    refetchInterval: 60_000,
  });
  const otherStatus = tab === 'changes_requested' || tab === 'approved' || tab === 'missed' ? tab : null;
  const monthScoped = tab === 'approved' || tab === 'missed';
  const other = useQuery({
    queryKey: ['teacher-reports', 'review', 'queue', otherStatus, monthScoped ? month : null] as const,
    enabled: otherStatus !== null,
    queryFn: ({ signal }) =>
      apiRequest<{ items: ReviewQueueItem[] }>('/api/teacher-reports/review', {
        signal,
        query: { status: otherStatus ?? undefined, month: monthScoped ? month : undefined },
      }),
  });

  const all = useMemo(() => submitted.data?.items ?? [], [submitted.data]);
  const flagged = all.filter(hasOpenFlags);
  const clean = all.filter((i) => !hasOpenFlags(i) && !isOwn(i));
  const active = otherStatus ? other : submitted;
  const items = tab === 'awaiting' ? all : tab === 'flagged' ? flagged : (other.data?.items ?? []);

  const bulk = useMutation({
    mutationFn: (ids: string[]) =>
      apiRequest<{ approved: string[]; skipped: Array<{ id: string; reason: string }> }>('/api/teacher-reports/review/bulk-approve', {
        method: 'POST',
        body: { ids },
      }),
    onSuccess: (r) => {
      toast(
        `Approved ${r.approved.length} report${r.approved.length === 1 ? '' : 's'}${r.skipped.length ? ` · ${r.skipped.length} skipped (flagged or already reviewed)` : ''}.`,
        { variant: 'success' },
      );
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['teacher-reports', 'review'] });
      void queryClient.invalidateQueries({ queryKey: ['teacher-reports', 'leaderboard'] });
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : 'Could not approve the reports.', { variant: 'error' }),
  });

  const selectedClean = [...selected].filter((id) => clean.some((c) => c.id === id));
  const allCleanSelected = clean.length > 0 && selectedClean.length === clean.length;
  const oldest = all[0];

  if (submitted.isError && isReportsDisabled(submitted.error)) {
    return (
      <ListShell title="Reports to review">
        <EmptyState
          title="Teacher reports are switched off"
          description={
            featuresPath
              ? `Switch on Teacher reports in ${featuresPath} to start reviewing.`
              : 'Ask a super admin to switch on Teacher reports.'
          }
          icon={<ShieldCheck size={32} aria-hidden="true" />}
        />
      </ListShell>
    );
  }

  return (
    <ListShell
      title="Reports to review"
      description="Approve reports to bank the teacher’s points. Flags are raised automatically when a report disagrees with portal data."
      primaryAction={
        monthScoped ? (
          <MonthPicker month={month} onChange={setMonth} />
        ) : (
          <Link to="/academics/report-leaderboard" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold-700 hover:underline dark:text-gold-300">
            <Trophy size={14} aria-hidden="true" /> Leaderboard
          </Link>
        )
      }
    >
      <div className="space-y-4">
      <KpiStrip
        className="lg:grid-cols-3!"
        loading={submitted.isLoading}
        items={[
          { label: 'Awaiting review', value: all.length, sub: oldest ? `Oldest: ${formatPeriod(oldest.kind, oldest.periodStart, oldest.periodKey)}` : 'All caught up' },
          { label: 'Flagged', value: <span className={clsx(flagged.length && 'text-[color:var(--color-danger)]')}>{flagged.length}</span>, sub: 'Need a closer look', onClick: () => setTab('flagged') },
          { label: 'Ready to approve', value: clean.length, sub: 'No flags raised' },
        ]}
      />

      <div role="tablist" aria-label="Report status" className="flex gap-1 overflow-x-auto border-b border-[color:var(--color-border)]">
        {TABS.map((t) => {
          const count = t.value === 'awaiting' ? all.length : t.value === 'flagged' ? flagged.length : 0;
          return (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={tab === t.value}
              onClick={() => {
                setTab(t.value);
                setExpanded(null);
              }}
              className={clsx(
                '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm',
                tab === t.value
                  ? 'border-gold-500 font-semibold text-[color:var(--color-text)]'
                  : 'border-transparent text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
              )}
            >
              {t.label}
              {count > 0 ? (
                <span className="ml-1.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-[color:var(--color-surface-strong)] px-1.5 text-[10px] font-semibold tabular-nums">
                  {count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {tab === 'awaiting' && clean.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-[18px] w-[18px] accent-gold-700"
              checked={allCleanSelected}
              onChange={(e) => setSelected(e.target.checked ? new Set(clean.map((c) => c.id)) : new Set())}
            />
            Select all unflagged
          </label>
          <Button
            size="sm"
            icon={<Check size={14} aria-hidden="true" />}
            disabled={selectedClean.length === 0}
            loading={bulk.isPending}
            onClick={() => bulk.mutate(selectedClean)}
          >
            Approve {selectedClean.length || ''} selected
          </Button>
        </div>
      ) : null}

      {active.isLoading ? (
        <div className="grid gap-3">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : active.isError ? (
        <ErrorState
          title="Could not load reports"
          description={active.error instanceof ApiError ? active.error.message : undefined}
          onRetry={() => void active.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title={tab === 'awaiting' ? 'All reviewed' : tab === 'flagged' ? 'No flagged reports' : 'Nothing here'}
          description={tab === 'awaiting' ? 'Nothing waiting — points are banked. New reports appear here as teachers submit them.' : undefined}
          icon={<Check size={32} aria-hidden="true" />}
        />
      ) : (
        <ul className="space-y-2">
          {items.map((i) => (
            <ReviewItem
              key={i.id}
              item={i}
              expanded={expanded === i.id}
              onToggle={() => setExpanded((e) => (e === i.id ? null : i.id))}
              selectable={tab === 'awaiting'}
              selected={selected.has(i.id)}
              onSelect={(on) =>
                setSelected((s) => {
                  const next = new Set(s);
                  if (on) next.add(i.id);
                  else next.delete(i.id);
                  return next;
                })
              }
              canExcuse={canExcuse}
              own={isOwn(i)}
            />
          ))}
        </ul>
      )}
      </div>
    </ListShell>
  );
}
